/**
 * client/src/features/income/hooks/useIncome.js
 * 
 * Custom hook for managing income state and operations.
 * Handles fetching, creating, updating, and deleting incomes.
 */
import { useState, useCallback, useEffect } from 'react';
import incomeService from '../services/incomeService';
import moneyBucketService from '../services/moneyBucketService';

const useIncome = (options = {}) => {
  const { isPartnerMode = false, includePartner = false } = options;
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [buckets, setBuckets] = useState([]);

  /**
   * Fetch all incomes
   */
  const fetchIncomes = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching income data...');
      
      // Pass partner access and includePartner flags to the service
      const response = await incomeService.getAll({ 
        partnerAccess: isPartnerMode,
        includePartner: includePartner && !isPartnerMode // Only include partner if not in partner mode
      });
      
      // Log the response structure to help with debugging
      console.log('🔍 Raw income response:', response);
      
      // Ensure we're handling the data correctly
      let incomesData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          incomesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          incomesData = response.data.data;
        }
      }
      
      // Normalize the income data to ensure consistent properties
      const normalizedIncomes = incomesData.map(income => ({
        ...income,
        // Ensure bucket is consistently referenced
        bucketId: income.bucketId || (income.bucket && (income.bucket._id || income.bucket)) || null,
        // Add a stringified version of the bucket ID for easier comparison
        bucketIdString: income.bucketId || 
          (income.bucket && (typeof income.bucket === 'object' ? 
            (income.bucket._id || String(income.bucket)) : 
            income.bucket)) || null,
        // Ensure order property exists
        order: income.order !== undefined ? income.order : 0
      }));
      
      console.log(`✅ Successfully fetched ${normalizedIncomes.length} income entries:`, normalizedIncomes);
      console.log(`🔬 Income data structure details:`, normalizedIncomes.map(inc => ({
        id: inc._id,
        name: inc.name,
        bucketId: inc.bucketId,
        bucketIdString: inc.bucketIdString,
        bucket: inc.bucket,
        order: inc.order
      })));
      
      // Update income state with normalized data
      setIncomes(normalizedIncomes);
      setError(null);
      return normalizedIncomes;
    } catch (err) {
      console.error('❌ Error fetching incomes:', err);
      setError('Failed to fetch incomes');
      return [];
    } finally {
      setLoading(false);
    }
  }, [isPartnerMode, includePartner]);

  /**
   * Auto-fetch incomes when the hook mounts or mode changes
   */
  useEffect(() => {
    console.log('🔄 useIncome: Auto-fetching incomes on mount/mode change', {
      isPartnerMode,
      includePartner
    });
    fetchIncomes();
  }, [fetchIncomes, isPartnerMode, includePartner]);

  /**
   * Fetch incomes for a specific bucket
   */
  const fetchBucketIncomes = useCallback(async (bucketId) => {
    try {
      setLoading(true);
      console.log(`🔄 Fetching incomes for bucket ${bucketId}...`);
      
      // Pass partner access flag to the service
      const response = await incomeService.getByBucket(bucketId, { partnerAccess: isPartnerMode });
      
      // Extract the incomes array from the response
      let bucketIncomes = [];
      if (response.data && Array.isArray(response.data)) {
        bucketIncomes = response.data;
      }
      
      // Ensure each income has an order property
      const normalizedBucketIncomes = bucketIncomes.map(income => ({
        ...income,
        order: income.order !== undefined ? income.order : 0
      }));
      
      // Sort the incomes by their order property
      const sortedBucketIncomes = normalizedBucketIncomes.sort((a, b) => {
        return a.order - b.order;
      });
      
      console.log(`✅ Successfully fetched ${sortedBucketIncomes.length} incomes for bucket:`, sortedBucketIncomes);
      
      // Update only the incomes for this bucket in the state
      setIncomes(prev => {
        const otherIncomes = prev.filter(income => 
          income.bucketId !== bucketId && 
          income.bucket !== bucketId && 
          !(income.bucket && income.bucket._id === bucketId)
        );
        return [...otherIncomes, ...sortedBucketIncomes];
      });
      
      setError(null);
      return sortedBucketIncomes;
    } catch (err) {
      console.error('❌ Error fetching bucket incomes:', err);
      setError('Failed to fetch bucket incomes');
      return [];
    } finally {
      setLoading(false);
    }
  }, [isPartnerMode, includePartner]);

  /**
   * Create a new income entry
   */
  const createIncome = useCallback(async (bucketId, incomeData) => {
    try {
      setLoading(true);
      console.log('🔄 Creating new income entry...', { bucketId, incomeData });
      
      // Include the bucketId in the income data for API
      const completeIncomeData = {
        ...incomeData,
        bucketId,
        bucket: bucketId // Ensure we set both bucket and bucketId
      };
      
      // Validate required fields
      const requiredFields = ['name', 'amount', 'category', 'frequency', 'bucket'];
      const missingFields = requiredFields.filter(field => {
        const value = completeIncomeData[field];
        return value === undefined || value === null || value === '';
      });
      
      if (missingFields.length > 0) {
        console.error('❌ Missing required fields:', missingFields);
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }
      
      // Ensure proper data types
      if (typeof completeIncomeData.amount !== 'number') {
        completeIncomeData.amount = parseFloat(completeIncomeData.amount) || 0;
      }
      
      if (typeof completeIncomeData.netAmount !== 'number') {
        completeIncomeData.netAmount = parseFloat(completeIncomeData.netAmount) || 0;
      }
      
      // Ensure the frequency is lowercase and matches allowed enum values
      if (completeIncomeData.frequency) {
        const normalizedFreq = completeIncomeData.frequency.toLowerCase();
        const allowedFrequencies = ['monthly', 'biweekly', 'weekly', 'yearly', 'onetime'];
        
        if (allowedFrequencies.includes(normalizedFreq)) {
          completeIncomeData.frequency = normalizedFreq;
        } else {
          // Map to closest valid frequency
          const frequencyMap = {
            'quarterly': 'yearly',
            'annual': 'yearly',
          };
          completeIncomeData.frequency = frequencyMap[normalizedFreq] || 'monthly';
        }
      }
      
      // Ensure netAmount is never negative
      if (completeIncomeData.netAmount < 0) {
        console.warn('⚠️ Correcting negative netAmount:', completeIncomeData.netAmount);
        completeIncomeData.netAmount = 0;
      }
      
      // Ensure category is uppercase
      if (completeIncomeData.category) {
        completeIncomeData.category = completeIncomeData.category.toUpperCase();
      }
      
      // Log the final data being sent to the API
      console.log('📝 Final income data being sent to API:', completeIncomeData);
      
      const response = await incomeService.create(completeIncomeData);
      const newIncome = response.data;
      console.log('✅ Income created successfully:', newIncome);
      
      // Update local state
      setIncomes(prev => {
        // Filter out any existing income with the same ID (shouldn't happen, but just in case)
        const filtered = prev.filter(income => income._id !== newIncome._id);
        return [...filtered, newIncome];
      });
      
      // Refresh the bucket's incomes to ensure consistency
      await fetchBucketIncomes(bucketId);
      
      setError(null);
      return newIncome;
    } catch (err) {
      console.error('❌ Error creating income:', err);
      // Log more detailed error information if available
      if (err.response) {
        console.error('❌ Error response from server:', {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers
        });
      }
      setError('Failed to create income');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchBucketIncomes]);

  /**
   * Update an existing income entry
   */
  const updateIncome = useCallback(async (bucketId, incomeId, incomeData) => {
    try {
      setLoading(true);
      console.log('🔄 Updating income entry...');
      
      // Include bucketId in the data if not already present
      const completeIncomeData = {
        ...incomeData,
        bucketId: bucketId || incomeData.bucketId
      };
      
      const response = await incomeService.update(incomeId, completeIncomeData);
      const updatedIncome = response.data;
      console.log('✅ Income updated successfully:', updatedIncome);
      
      // Update local state
      setIncomes(prev => 
        prev.map(income => 
          income._id === incomeId ? updatedIncome : income
        )
      );
      
      setError(null);
      return updatedIncome;
    } catch (err) {
      console.error('❌ Error updating income:', err);
      setError('Failed to update income');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Switch jobs: create a new income and deactivate the old one
   */
  const switchJob = useCallback(async (incomeId, data) => {
    try {
      setLoading(true);
      const response = await incomeService.switchJob(incomeId, data);
      const { oldIncome, newIncome } = response.data || {};

      // Update local state: replace old, append new
      setIncomes(prev => {
        const withoutOld = prev.map(inc => inc._id === oldIncome?._id ? oldIncome : inc);
        return newIncome ? [...withoutOld, newIncome] : withoutOld;
      });

      setError(null);
      return response.data;
    } catch (err) {
      console.error('❌ Error switching job:', err);
      setError('Failed to switch job');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete an income entry
   * @param {string} incomeIdOrObject - Income ID or income object
   * @param {string} [optionalIncomeId] - Optional income ID if first param is bucketId
   * @returns {Promise} Promise resolved when income is deleted
   */
  const deleteIncome = useCallback(async (incomeIdOrObject, optionalIncomeId) => {
    let incomeId;
    let bucketId;
    
    // Check if we're using (bucketId, incomeId) or just (incomeId) format
    if (optionalIncomeId) {
      // Format: deleteIncome(bucketId, incomeId)
      bucketId = incomeIdOrObject;
      incomeId = optionalIncomeId;
    } else {
      // Format: deleteIncome(incomeId) or deleteIncome(incomeObject)
      if (typeof incomeIdOrObject === 'object') {
        // It's an income object
        incomeId = incomeIdOrObject._id;
        bucketId = incomeIdOrObject.bucketId || 
          (incomeIdOrObject.bucket?._id) || 
          incomeIdOrObject.bucket;
      } else {
        // It's just the income ID
        incomeId = incomeIdOrObject;
        
        // Try to find the income in our state to get bucketId
        const income = incomes.find(inc => inc._id === incomeId);
        if (income) {
          bucketId = income.bucketId || 
            (income.bucket?._id) || 
            income.bucket;
        }
      }
    }
    
    // Validate that we have an incomeId
    if (!incomeId) {
      console.error('❌ Cannot delete income: Missing income ID');
      throw new Error('Missing income ID for deletion');
    }
    
    // Debug log to help troubleshoot
    console.log(`🗑️ Deleting income ${incomeId}${bucketId ? ` from bucket ${bucketId}` : ''}`);
    
    try {
      setLoading(true);
      
      // Optimistically update local state before API call
      setIncomes(prev => prev.filter(income => income._id !== incomeId));
      
      // Send the API request to delete the income
      await incomeService.remove(incomeId);
      console.log('✅ Income deleted successfully');
      
      setError(null);
      return true;
    } catch (err) {
      console.error('❌ Error deleting income:', err);
      
      // Revert optimistic update on error
      const incomesData = await incomeService.getAll();
      setIncomes(incomesData);
      
      setError('Failed to delete income');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [incomes, setIncomes, setLoading, setError]);

  /**
   * Process a pay stub for an income
   */
  const processPayStub = useCallback(async (incomeId, file) => {
    try {
      setLoading(true);
      console.log('🔄 Processing pay stub...');
      
      // Create form data for the file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('incomeId', incomeId);
      
      const response = await incomeService.processPayStub(formData);
      const result = response.data;
      console.log('✅ Pay stub processed successfully:', result);
      
      // Update the income in local state with new pay stub data
      setIncomes(prev => 
        prev.map(income => 
          income._id === incomeId 
            ? { ...income, payStubs: [...(income.payStubs || []), result] }
            : income
        )
      );
      
      setError(null);
      return result;
    } catch (err) {
      console.error('❌ Error processing pay stub:', err);
      setError('Failed to process pay stub');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reorder incomes within a bucket
   * @param {string} bucketId - Bucket ID
   * @param {Array} reorderedIncomes - Array of income objects in their new order
   * @returns {Promise} Promise resolved when incomes are reordered
   */
  const reorderIncomes = useCallback(async (bucketId, reorderedIncomes) => {
    try {
      setLoading(true);
      console.log(`🔄 Reordering incomes in bucket ${bucketId}...`);
      
      // Log the reorderedIncomes array to verify it has the correct structure
      console.log('🔍 Reordered incomes received:', reorderedIncomes);
      
      // Prepare order updates
      const orderUpdates = reorderedIncomes.map((income, index) => {
        // Log each income object to verify it has an _id
        console.log(`🔍 Processing income ${index}:`, { _id: income._id, order: index });
        
        return {
          _id: income._id,
          order: index
        };
      });
      
      // Log the final orderUpdates array
      console.log('🔍 Final order updates:', orderUpdates);
      
      // Update incomes on the server
      await incomeService.reorderIncomes(bucketId, orderUpdates);
      
      // Update local state
      setIncomes(prev => {
        // Keep incomes from other buckets unchanged
        const otherIncomes = prev.filter(income => 
          income.bucketId !== bucketId && 
          income.bucket !== bucketId && 
          !(income.bucket && income.bucket._id === bucketId)
        );
        
        // Add reordered incomes with updated order property
        const updatedReorderedIncomes = reorderedIncomes.map((income, index) => ({
          ...income,
          order: index
        }));
        
        return [...otherIncomes, ...updatedReorderedIncomes];
      });
      
      console.log('✅ Incomes reordered successfully');
      setError(null);
      return reorderedIncomes;
    } catch (err) {
      console.error('❌ Error reordering incomes:', err);
      setError('Failed to reorder incomes');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a bucket and its associated incomes
   * @param {string} bucketId - ID of the bucket to delete
   * @param {string} bucketName - Name of the bucket (for notifications)
   * @returns {Promise} Promise resolved when bucket is deleted
   */
  const deleteBucket = useCallback(async (bucketId, bucketName) => {
    if (!bucketId) {
      console.error('❌ Cannot delete bucket: Missing bucket ID');
      throw new Error('Missing bucket ID for deletion');
    }

    console.log(`🗑️ Attempting to delete bucket: ${bucketName} (${bucketId})`);

    try {
      setLoading(true);

      // Optimistically update UI state before API call
      setBuckets(prev => prev.filter(bucket => bucket._id !== bucketId));
      setIncomes(prev => prev.filter(income => {
        const incomeBucketId = income.bucketId || income.bucket?._id || income.bucket;
        return incomeBucketId !== bucketId;
      }));

      // Make API call
      await moneyBucketService.delete(bucketId);
      console.log(`✅ Successfully deleted bucket: ${bucketName}`);

      setError(null);
      return true;
    } catch (err) {
      console.error(`❌ Error deleting bucket ${bucketId}:`, err);
      
      // Revert optimistic updates on error
      const [bucketsData, incomesData] = await Promise.all([
        moneyBucketService.getAll(),
        incomeService.getAll()
      ]);
      setBuckets(bucketsData);
      setIncomes(incomesData);
      
      setError('Failed to delete bucket');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setBuckets, setIncomes, setLoading, setError]);

  /**
   * Add a new bucket with optimistic updates
   * @param {Object} bucketData - Data for the new bucket
   * @returns {Promise<Object>} Promise resolved with the new bucket
   */
  const addBucket = useCallback(async (bucketData) => {
    try {
      setLoading(true);
      
      // Create a temporary ID for optimistic update
      const tempId = `temp-${Date.now()}`;
      const optimisticBucket = {
        _id: tempId,
        ...bucketData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Optimistically add the bucket to state
      setBuckets(prev => [...prev, optimisticBucket]);
      
      // Make the API call
      const newBucket = await moneyBucketService.create(bucketData);
      console.log('✅ Bucket created successfully:', newBucket);
      
      // Update the state with the real bucket data
      setBuckets(prev => prev.map(bucket => 
        bucket._id === tempId ? newBucket : bucket
      ));
      
      setError(null);
      return newBucket;
    } catch (err) {
      console.error('❌ Error creating bucket:', err);
      
      // Revert optimistic update on error
      const bucketsData = await moneyBucketService.getAll();
      setBuckets(bucketsData);
      
      setError('Failed to create bucket');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setBuckets, setLoading, setError]);

  return {
    incomes,
    loading,
    error,
    buckets,
    addBucket,
    deleteBucket,
    deleteIncome,
    fetchIncomes,
    fetchBucketIncomes,
    createIncome,
    updateIncome,
    switchJob,
    processPayStub,
    reorderIncomes
  };
};

export default useIncome; 